import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ReportHeader, SummaryCards, FilterPanel, ExportButtons } from '../../components/reports';
import { D3BarChart } from '../../components/charts';
import { getRegionalAnalysis } from '../../services/project750kService';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';

const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ChartSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    font-family: 'Segoe UI', sans-serif;
  }

  th {
    background-color: #0077B5;
    color: white;
    font-weight: 600;
  }

  tbody tr {
    cursor: pointer;
    
    &:hover {
      background-color: #e3f2fd;
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 18px;
  color: #0077B5;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoBanner = styled.div`
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
`;

const RegionalAnalysis = () => {
  const navigate = useNavigate();
  const [counties, setCounties] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ product: '', lender: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (appliedFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const result = await getRegionalAnalysis(appliedFilters);
      setCounties(result.counties || []);
      setChartData(result.chartData || []);
      setSummary(result.summary || {});
      setMeta(result.meta || null);
    } catch (err) {
      setError(`Error loading regional analysis: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    // Debounce and reload data
    setTimeout(() => loadData(newFilters), 500);
  };

  const handleCountyClick = (county) => {
    // Navigate to debtor lookup with county pre-filled in future enhancement
    console.log('County clicked:', county);
  };

  const handleExportExcel = useCallback(async () => {
    try {
      await exportToExcel(counties, 'Regional_Analysis', 'Counties', filters);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  }, [counties, filters]);

  const handleExportPDF = useCallback(async () => {
    try {
      await exportToPDF({
        title: 'Regional Analysis Report',
        data: counties.slice(0, 100),
        chartElementId: 'regional-chart',
        summary: {
          'Top County': summary?.topCounty || 'N/A',
          'Total Counties': summary?.totalCounties || 0,
          'Total Filings (Top 20)': chartData.reduce((sum, c) => sum + c.total, 0)
        },
        filename: 'Regional_Analysis',
        filters
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    }
  }, [counties, summary, chartData, filters]);

  const filterConfig = [
    {
      type: 'text',
      label: 'Product Type',
      value: filters.product,
      onChange: (value) => handleFilterChange('product', value),
      placeholder: 'e.g., CATTLE, CORN'
    },
    {
      type: 'text',
      label: 'Lender Name',
      value: filters.lender,
      onChange: (value) => handleFilterChange('lender', value),
      placeholder: 'Search by lender'
    }
  ];

  const summaryCards = summary ? [
    { label: 'Total Counties', value: summary.totalCounties },
    { label: 'Top County', value: summary.topCounty },
    { label: 'Total Filings', value: counties.reduce((sum, c) => sum + c.total, 0) }
  ] : [];

  // Prepare bar chart data
  const barChartData = chartData.map(c => ({
    label: c.county,
    value: c.total
  }));

  if (loading) return <LoadingSpinner>Loading regional analysis...</LoadingSpinner>;

  return (
    <PageContainer>
      <ReportHeader icon="🗺️" title="Regional Analysis" />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {meta && (
        <InfoBanner>
          ℹ️ Analysis based on <strong>{meta.recordsScanned?.toLocaleString() || 0} records</strong>
          {meta.hasMore && ' (sampling representative subset)'}
        </InfoBanner>
      )}

      <FilterPanel filters={filterConfig} />
      
      <SummaryCards cards={summaryCards} />
      
      <ExportButtons
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        loading={loading}
      />

      <ChartSection id="regional-chart">
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Top 20 Counties by Filing Volume</h3>
        <D3BarChart
          data={barChartData}
          xKey="label"
          yKey="value"
          xLabel="County"
          yLabel="Total Filings"
          onClick={handleCountyClick}
          height="500px"
        />
      </ChartSection>

      <TableContainer>
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>
          All Counties ({counties.length} total)
        </h3>
        <Table>
          <thead>
            <tr>
              <th>County</th>
              <th>LB4 Filings</th>
              <th>ListC Filings</th>
              <th>Total Filings</th>
            </tr>
          </thead>
          <tbody>
            {counties.map((county, index) => (
              <tr key={index} onClick={() => handleCountyClick(county)}>
                <td><strong>{county.county}</strong></td>
                <td>{county.lb4.toLocaleString()}</td>
                <td>{county.listc.toLocaleString()}</td>
                <td><strong>{county.total.toLocaleString()}</strong></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default RegionalAnalysis;

